import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { imageOk, useAuthedSrc } from "@/lib/authedImage";

const skipMsg = (name) => `${name} wasn't added: use a JPG, PNG, WEBP or HEIC under 15 MB`;

function Photo({ hubId, p, onDelete }) {
  const src = useAuthedSrc(`/organizations/${hubId}/photos/${p.id}/file`);
  return (
    <figure className="group relative aspect-square overflow-hidden rounded-xl border border-q-border bg-q-raised" data-testid={`family-photo-${p.id}`}>
      {src ? <img src={src} alt={p.filename} className="h-full w-full object-cover" /> : <Loader2 className="absolute inset-0 m-auto h-5 w-5 animate-spin text-q-faint" />}
      <figcaption className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/75 to-transparent px-2.5 pb-2 pt-6">
        <span className="truncate text-[11px] font-semibold text-white" data-testid={`family-photo-by-${p.id}`}>{p.added_by_name}</span>
        {p.can_delete && (
          <button className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-black/50 text-white transition-colors hover:bg-red-600"
            onClick={() => onDelete(p)} title="Delete photo" aria-label="Delete photo" data-testid={`family-photo-delete-btn-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        )}
      </figcaption>
    </figure>
  );
}

export default function FamilyPhotos({ hub }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipped, setSkipped] = useState([]);
  const ref = useRef(null);
  const load = () => api.get(`/organizations/${hub.id}/photos`).then(({ data: d }) => setData(d));
  useEffect(() => { load(); }, [hub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (list.length > 20) { setSkipped([{ filename: "", message: "Add up to 20 photos at a time" }]); return; }
    const ok = list.filter(imageOk);
    const bad = list.filter((f) => !imageOk(f)).map((f) => ({ filename: f.name, message: skipMsg(f.name) }));
    setSkipped(bad);
    if (!ok.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      ok.forEach((f) => fd.append("files", f));
      const { data: res } = await api.post(`/organizations/${hub.id}/photos`, fd);
      setSkipped([...bad, ...res.skipped]);
      if (res.added.length) toast.success(`${res.added.length} photo${res.added.length > 1 ? "s" : ""} added`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  const remove = async (p) => {
    try {
      await api.delete(`/organizations/${hub.id}/photos/${p.id}`);
      setData((d) => ({ ...d, photos: d.photos.filter((x) => x.id !== p.id) }));
      toast.success("Photo removed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section data-testid="family-photos">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="q-eyebrow">Family photos · {hub.name}</h2>
          <p className="mt-1 text-sm text-q-muted">Shared with everyone in this hub. <span data-testid="family-photos-count">{data ? data.photos.length : "…"}</span> photos.</p>
        </div>
        {data?.can_add && (
          <>
            <button className="q-btn q-btn-secondary" onClick={() => ref.current?.click()} disabled={busy} data-testid="family-photos-add-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Add photos
            </button>
            <input ref={ref} type="file" multiple className="hidden" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/*" onChange={(e) => add(e.target.files)} data-testid="family-photos-input" />
          </>
        )}
      </div>
      {skipped.length > 0 && (
        <ul className="mb-4 space-y-1 rounded-lg border border-red-500/30 bg-red-500/10 px-3.5 py-2.5 text-sm font-semibold text-red-300" data-testid="family-photos-skipped">
          {skipped.map((s, i) => <li key={i}>{s.message}</li>)}
        </ul>
      )}
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-q-faint" /> : data.photos.length === 0 ? (
        <p className="rounded-xl border border-dashed border-q-line px-5 py-8 text-center text-sm text-q-muted" data-testid="family-photos-empty">No family photos yet. Add some for the slideshow.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" data-testid="family-photos-grid">
          {data.photos.map((p) => <Photo key={p.id} hubId={hub.id} p={p} onDelete={remove} />)}
        </div>
      )}
    </section>
  );
}
